import { useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from 'framer-motion';
import useGsapSection from '../../hooks/useGsapSection';
import { ScrambleReveal, ClipRevealText } from '../TextEffects';
import { projects as projectRegistry } from '../../data/projects';
import './ProjectsScroller.css';

const SCROLL_VH_PER_PROJECT = 120;

const projects = projectRegistry.map((p) => ({
  id: p.slug,
  index: p.index,
  title: p.title,
  discipline: p.discipline,
  year: p.year,
  blurb: p.blurb,
  media: p.media,
  link: p.path,
}));

function progressToIndex(progress, count) {
  if (count <= 1) return 0;
  if (progress <= 0) return 0;
  if (progress >= 1) return count - 1;
  return Math.min(count - 1, Math.floor(progress * count));
}

/* ── Clip-path keyframes — "window opening" from the corner ── */
const clipFrames = {
  initial: { clipPath: 'inset(0% 100% 100% 0%)', WebkitClipPath: 'inset(0% 100% 100% 0%)' },
  animate: { clipPath: 'inset(0% 0% 0% 0%)',     WebkitClipPath: 'inset(0% 0% 0% 0%)' },
  exit:    { clipPath: 'inset(100% 0% 0% 100%)', WebkitClipPath: 'inset(100% 0% 0% 100%)' },
};

const ProjectsScroller = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const headerInView = useGsapSection(headerRef);
  const scrollIndexRef = useRef(0);
  const [scrollIndex, setScrollIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const scrollIndexMotion = useTransform(scrollYProgress, (latest) =>
    progressToIndex(latest, projects.length)
  );

  useMotionValueEvent(scrollIndexMotion, 'change', (next) => {
    if (next === scrollIndexRef.current) return;
    scrollIndexRef.current = next;
    setScrollIndex(next);
  });

  const activeIndex = scrollIndex;
  const activeProject = useMemo(() => projects[activeIndex], [activeIndex]);

  return (
    <section
      ref={sectionRef}
      id="projects"
      className="ps-section"
      style={{ height: `${SCROLL_VH_PER_PROJECT * projects.length}vh` }}
      data-testid="projects-scroller"
    >
      <div className="ps-sticky">
        {/* Section header */}
        <div className="ps-header" ref={headerRef}>
          <span className="ps-header-label gsap-reveal">
            <span className="ps-header-num">(003)</span>
            <span className="ps-header-divider" />
            <ClipRevealText text="Selected Works" delay={0.05} externalInView={headerInView} />
            {' '}
            <ScrambleReveal text="/ 2026" delay={0.15} externalInView={headerInView} />
          </span>
          <span className="ps-header-counter tabular gsap-reveal">
            <span className="ps-header-counter-now">{activeProject.index}</span>
            <span className="ps-header-counter-sep">/</span>
            <span className="ps-header-counter-total">{String(projects.length).padStart(2, '0')}</span>
          </span>
        </div>

        {/* Two-column body */}
        <div className="ps-body">
          {/* Left: titles list */}
          <ol
            className="ps-list"
            role="list"
            onMouseLeave={() => {
              const next = progressToIndex(scrollYProgress.get(), projects.length);
              scrollIndexRef.current = next;
              setScrollIndex(next);
            }}
          >
            {projects.map((p, i) => {
              const isActive = i === activeIndex;
              return (
                <li
                  key={p.id}
                  className={`ps-item${isActive ? ' is-active' : ''}`}
                  onMouseEnter={() => {
                    scrollIndexRef.current = i;
                    setScrollIndex(i);
                  }}
                  data-testid={`ps-item-${p.index}`}
                >
                  <span className="ps-item-num">{p.index}</span>
                  <Link to={p.link} className="ps-item-title-wrap" aria-label={`Open ${p.title}`}>
                    <span className="ps-item-title">{p.title}</span>
                    <span className="ps-item-arrow" aria-hidden="true">↗</span>
                  </Link>
                  <span className="ps-item-meta">
                    <span>{p.discipline}</span>
                    <span className="ps-item-dot">·</span>
                    <span>{p.year}</span>
                  </span>

                  {isActive && <p className="ps-item-blurb">{p.blurb}</p>}
                </li>
              );
            })}
          </ol>

          {/* Right: media frame with clip-path reveal */}
          <div className="ps-frame">
            <div className="ps-frame-inner">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={activeProject.id}
                  className="ps-media"
                  initial={clipFrames.initial}
                  animate={clipFrames.animate}
                  exit={clipFrames.exit}
                  transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
                >
                  <motion.img
                    src={activeProject.media}
                    alt={activeProject.title}
                    loading="lazy"
                    initial={{ scale: 1.08 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 1.02 }}
                    transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  />
                  <div className="ps-media-overlay" aria-hidden="true" />

                  {/* Floating caption inside frame */}
                  <div className="ps-media-tag">
                    <span className="ps-media-tag-num">{activeProject.index}</span>
                    <span className="ps-media-tag-divider" />
                    <span className="ps-media-tag-name">{activeProject.title}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Caption under frame */}
            <div className="ps-caption">
              <span className="ps-caption-side">
                {activeProject.index} — {activeProject.discipline}
              </span>
              <Link to={activeProject.link} className="ps-caption-cta" data-testid={`view-${activeProject.id}`}>
                <span className="ps-caption-cta-text">View case study</span>
                <span className="ps-caption-cta-arrow" aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="ps-progress" aria-hidden="true">
          <motion.div
            className="ps-progress-bar"
            style={{ scaleX: scrollYProgress }}
          />
        </div>
      </div>
    </section>
  );
};

export default ProjectsScroller;
